// @ts-nocheck
import fetch from 'unfetch';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/react';

import CONSTANTS from 'src/store/constants';

export { default as formatDate } from './date_time_format';
export { default as prettyBytes } from './pretty_bytes';
export { default as uploadFile } from './xhr_upload';

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const getOnboardingStatusDescription = (state) => {
  switch (state) {
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD_FAILURE:
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD_FAILURE:
    case CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET_FAILURE:
    case CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL_FAILURE:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_CLUSTERS_FAILURE:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_HISTOGRAM_FAILURE:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_DATA_ENRICHMENT_FAILURE:
      return CONSTANTS.ONBOARDING_STATUS.DESC.ERROR;
    case CONSTANTS.SEGMENT.STATUS.PREDICT_SCORES_INITIATED:
    case CONSTANTS.SEGMENT.STATUS.PREDICT_SCORES_SUCCESS:
    case CONSTANTS.SEGMENT.STATUS.PREDICT_SCORES_FAILURE:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_SCORES_DOWNLOAD_SUCCESS:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_SCORES_DOWNLOAD_INITIATED:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_SCORES_DOWNLOAD_FAILURE:
    case CONSTANTS.SEGMENT.STATUS.NOTIFICATION_SUCCESS:
      return CONSTANTS.ONBOARDING_STATUS.DESC.ONBOARDED;
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD:
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_CLIENT_UPLOAD:
      return CONSTANTS.ONBOARDING_STATUS.DESC.RECEIVED;
    case CONSTANTS.ONBOARDING_STATUS.API.ACCEPT_CLIENT_UPLOAD:
      return CONSTANTS.ONBOARDING_STATUS.DESC.READY;
    case CONSTANTS.ONBOARDING_STATUS.API.DET_WAITING_FOR_CONFIRMATION:
      return CONSTANTS.ONBOARDING_STATUS.DESC.USER_CONFIRMATION_NEEDED;
    case CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET:
      return CONSTANTS.ONBOARDING_STATUS.DESC.VALIDATED;
    case CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL:
      return CONSTANTS.ONBOARDING_STATUS.DESC.ADMIN_APPROVAL_NEEDED;
    case CONSTANTS.ONBOARDING_STATUS.API.APPROVED_BY_ADMIN:
      return CONSTANTS.ONBOARDING_STATUS.DESC.APPROVED;
    case CONSTANTS.ONBOARDING_STATUS.API.EXTRACTED_FOR_PUSH:
    case CONSTANTS.ONBOARDING_STATUS.API.PACKAGED_FOR_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.PUSHED_TO_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.READY_FOR_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_FROM_PROVIDER:
    case CONSTANTS.ONBOARDING_STATUS.API.PROFILE_MATCHED:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_CLUSTERS_INITIATED:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_CLUSTERS_SUCCESS:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_HISTOGRAM_INITIATED:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_HISTOGRAM_SUCCESS:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_DATA_ENRICHMENT_INITIATED:
    case CONSTANTS.SEGMENT.STATUS.GENERATE_DATA_ENRICHMENT_SUCCESS:
    default:
      // NOTE: All states not explicitly mentioned are in processing
      // this is to hide failures from clients that they can nothing about
      return CONSTANTS.ONBOARDING_STATUS.DESC.PROCESSING;
  }
};

/* eslint-disable no-restricted-globals */
export const noOp = () => {
  // Included to handle no-ops
};

const ONPOINT_AUTH = process.env.REACT_APP_ONPOINT_AUTH || 'http://localhost:9000';

const home = () => {
  window.location = '/';
};

export const inlineLogin = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location = '/';
};

export const logout = async () => {
  try {
    await fetch(`${ONPOINT_AUTH}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({}),
    });
  } catch (error) {
    // Ignoring error during logout
  }
  inlineLogin();
  /**
   *
   * Deprecating logout over inline logout
  let domain = 'onpoint';
  if (location.hostname !== 'localhost') {
    domain = location.hostname.split('.')[0].replace('stage-', '');
  }
  window.location = `${ONPOINT_AUTH}/${domain}/logout`;
  */
};

const refresh = async (username, accessToken, refreshToken) => {
  const refreshResp = await fetch(`${ONPOINT_AUTH}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      accessToken,
      refreshToken,
    }),
  });
  if (refreshResp.status === 200) {
    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = await refreshResp.json();
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
  } else {
    inlineLogin();
  }
};

/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */

const RETRIES = 3;

const call = async (endpoint, options = { method: 'GET', headers: {} }) => {
  let attempts = 0;
  let hasAuthErrors = false;
  let resp = null;
  let serverTimeout = false;
  while (attempts < RETRIES && !serverTimeout) {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const username = localStorage.getItem('username');
      if (!accessToken || !refreshToken) {
        window.location.href = '/login';
      }
      if (!options.headers) {
        options.headers = {};
      }
      // Don't send headers for mock server
      if (!endpoint.startsWith(CONSTANTS.MOCK)) {
        options.headers.Authorization = `Bearer ${accessToken}`;
      }
      resp = await fetch(endpoint, options);
      if (resp.status <= 399) {
        // status < 399 -> Everything okay
        return resp;
      }
      if (resp.status === 401) {
        // Maybe the token is expired. Refresh and try again
        hasAuthErrors = true;
        await refresh(username, accessToken, refreshToken);
      } else if (resp.status === 504) {
        // We've hit a gateway timeout. We should not try anymore
        serverTimeout = true;
      } else if (resp.status >= 502) {
        // BadGateway or Service Unavailable or server errors
        // Lets retry after sleeping
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
      } else if (resp.status === 500 || resp.status === 501) {
        // Seems like an issue on the server side or is not implemented
        // in the server side. We throw an ApiError
        const message = 'We encountered errors when processing your request.';
        throw new ApiError(message, resp.status);
      } else {
        // status is 4xx -> Bad Input, Don't have to retry
        // throw an error
        const contentType = resp.headers.get('content-type');
        let message = await resp.text();
        if (contentType.toLowerCase().startsWith('application/json')) {
          const data = await resp.json();
          if (data.message) {
            message = data.message;
          }
          if (data.errors) {
            message = `We encountered following errors when processing your request: ${data.errors.join(',')}`;
          }
        }
        throw new ApiError(message, resp.status);
      }
    } catch (errorOrEvent) {
      let error = errorOrEvent;
      let sentryError = errorOrEvent;
      if (errorOrEvent.target && errorOrEvent.type) {
        // errorOrEvent maynot be a error object but a
        // javascript event object in which case
        // lets convert it to error
        sentryError = new Error(`Received an unhandled error event on target ${errorOrEvent.target}`);
        // We treat the unhandled error as a 500 error to the client
        error = new ApiError('We encountered errors when processing your request.', 500);
      }
      // We capture the error on Sentry.
      Sentry.captureException(sentryError);
      throw error;
    }
    attempts += 1;
  }
  if (hasAuthErrors) {
    // Handler Error
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    await logout();
  }
  // all retries are over and the response is not <=399.
  // Lets raise an error based on last response status
  const error = new ApiError(`Fetch gave up after ${RETRIES}`, resp?.status);
  Sentry.captureException(error);
  throw error;
};

/* eslint-enable no-param-reassign */
/* eslint-enable no-await-in-loop */

export const get = async (endpoint, options = {}) => {
  const annotatedOptions = {
    ...options,
    method: 'GET',
  };

  let response = await call(endpoint, annotatedOptions);
  response = await response.json();

  const url = new URL(endpoint);
  const urlParams = new URLSearchParams(url.search);
  const queryHasPage = urlParams.has('page');

  if (!queryHasPage && response.pages && response.pages > 1) {
    let allData = response.items;
    let currentPage = response.page + 1;
    const totalPages = response.pages;

    while (currentPage <= totalPages) {
      urlParams.set('page', currentPage);
      url.search = urlParams.toString();
      // eslint-disable-next-line no-await-in-loop
      response = await call(url.toString(), annotatedOptions);
      // eslint-disable-next-line no-await-in-loop
      response = await response.json();
      allData = allData.concat(response.items || []);
      currentPage += 1;
    }

    return { ...response, items: allData };
  }

  return response;
};

export const post = async (endpoint, payload, options) => {
  const resp = await call(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options?.headers ?? {}),
    },
    body: JSON.stringify(payload),
  });
  const contentType = resp.headers.get('content-type');
  if (resp.status === 204 || resp.status === 202) return null;
  if (contentType.toLowerCase().includes('application/json')) {
    return resp.json();
  }
  return resp.text();
};

export const patch = async (endpoint, payload) => {
  const resp = await call(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const contentType = resp.headers.get('content-type');
  if (resp.status === 204 || resp.status === 202) return null;
  if (contentType.toLowerCase().includes('application/json')) {
    return resp.json();
  }
  return resp.text();
};

// NOTE: Typically DELETE can take a body but we don't support it
export const del = async (endpoint) => call(endpoint, { method: 'DELETE' });

export const login = () => {
  inlineLogin();
  /**
   *
   * Deprecating login for inlineLogin
  let domain = 'onpoint';
  if (location.hostname !== 'localhost') {
    domain = location.hostname.split('.')[0].replace('stage-', '');
  }
  window.location = `${ONPOINT_AUTH}/${domain}/login`;
  */
};

export const trunc = (word, letters = 16) => (word.length > letters ? `${word.slice(0, letters)}...` : word);

export const capitalize = ([first, ...rest], locale = navigator.language) =>
  [first.toLocaleUpperCase(locale), ...rest].join('');

export const tokenExchange = async (code) => {
  let domain = 'onpoint';
  if (location.hostname !== 'localhost') {
    domain = location.hostname.split('.')[0].replace('stage-', '');
  }
  const authResp = await fetch(`${ONPOINT_AUTH}/${domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
    }),
  });
  if (authResp.status === 200) {
    const { accessToken, refreshToken } = await authResp.json();
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    // Get the profile information as well
    const { username, givenName, familyName } = await get(`${ONPOINT_AUTH}/profile`);
    localStorage.setItem('username', username);
    localStorage.setItem('givenName', givenName);
    localStorage.setItem('familyName', familyName);
    home();
  } else {
    login();
  }
};

/* eslint-disable camelcase */
export const tokenExchangeSSO = async (code, redirect_uri, clientId) => {
  const authResp = await fetch(`${ONPOINT_AUTH}/oauth2/token/${clientId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri,
      /* eslint-enable camelcase */
    }),
  });
  if (authResp.status === 200) {
    const { accessToken, refreshToken } = await authResp.json();
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    // Get the profile information as well
    const { username, givenName, familyName } = await get(`${ONPOINT_AUTH}/profile`);
    localStorage.setItem('username', username);
    localStorage.setItem('givenName', givenName);
    localStorage.setItem('familyName', familyName);
    home();
  } else {
    login();
  }
};

export const switchClient = async (tenant) => {
  const { accessToken } = await post(`${ONPOINT_AUTH}/service/switch`, { client: tenant });
  localStorage.setItem('accessToken', accessToken);
};

// source: https://blog.abelotech.com/posts/number-currency-formatting-javascript

/**
 * formatNumber - simple number formatter to number with commas
 * @param {number} num
 * @return {string | undefined}
 */
export const formatNumber = (num) => num?.toString?.().replace?.(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

/**
 * Format a number with commas, returns NaN if the number cannot be converted
 * @param {string|number} num
 * @param {boolean} rounded
 * @param {boolean} restrictNaN - when a NaN would be returned, instead return null
 * @returns {number|string}
 */
export const formatNumberV2 = (num, rounded = false, restrictNaN = false) => {
  // eslint-disable-next-line no-param-reassign
  if (typeof num === 'string') num = +num;
  if ([null, undefined].includes(num) || !Number.isFinite(num)) return restrictNaN ? null : Number.NaN;
  // eslint-disable-next-line no-param-reassign
  if (rounded) num = Math.round(num);
  if (Number.isInteger(num)) return `${num}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  const [first, second] = `${num}`.split('.');
  const parts = [first.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'), second];
  return parts.join('.');
};

/**
 * formatPercent - format a number with a percent sign
 * @param {number|string} num
 * @param {boolean} spacedSymbol - whether the number should have a space before the percent sign
 * @return {string|null}
 */
export const formatPercent = (num, spacedSymbol = true) => {
  if ([null, undefined].includes(num)) return null;
  // eslint-disable-next-line no-param-reassign
  if (typeof num === 'string') num = Number(num);
  if (!num && !Number.isFinite(num)) return null;
  return `${num.toFixed(1)}${spacedSymbol ? ' %' : '%'}`;
};
/* eslint-disable no-bitwise */

// copied from https://gist.github.com/jed/982883. This gist is a good read

export const uuid = (a) =>
  a
    ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
    : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, uuid);

/* eslint-enable no-bitwise */

// Shamelessly copied from https://github.com/Gozala/querystring/blob/master/decode.js
// to reduce bundle size

/* eslint-disable */

export const parseQs = (qs, sep, eq, options) => {
  sep = sep || '&';
  eq = eq || '=';
  const obj = {};
  if (typeof qs !== 'string' || qs.length === 0) {
    return obj;
  }
  const regexp = /\+/g;
  qs = qs.split(sep);
  let maxKeys = 1000;
  if (options && typeof options.maxKeys === 'number') {
    maxKeys = options.maxKeys;
  }
  let len = maxKeys <= 0 ? qs.length : Math.min(qs.length, maxKeys);
  // maxKeys <= 0 means that we should not limit keys count
  // if (maxKeys > 0 && len > maxKeys) {
  // 	len = maxKeys;
  // }
  for (let i = 0; i < len; ++i) {
    const x = qs[i].replace(regexp, '%20');
    const idx = x.indexOf(eq);
    let kstr;
    let vstr;
    let k;
    let v;
    if (idx >= 0) {
      kstr = x.substr(0, idx);
      vstr = x.substr(idx + 1);
    } else {
      kstr = x;
      vstr = '';
    }
    k = decodeURIComponent(kstr);
    v = decodeURIComponent(vstr);
    if (!Object.hasOwn(obj, k)) {
      obj[k] = v;
    } else if (Array.isArray(obj[k])) {
      obj[k].push(v);
    } else {
      obj[k] = [obj[k], v];
    }
  }
  return obj;
};

/* eslint-enable */

export const arrayIfScalar = (value) => {
  if ([null, undefined].includes(value)) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export const scalarIfUnitArray = (value) => (Array.isArray(value) && value.length === 1 ? value[0] : null);

export const themeFromTag = ({ tag, theme }) => {
  switch (tag) {
    case CONSTANTS.SEGMENT.TAG.HIT:
      return CONSTANTS.SEGMENT.TAG.HIT.toLowerCase();
    case CONSTANTS.SEGMENT.TAG.CLUSTER:
      return CONSTANTS.SEGMENT.TAG.HIT.toLowerCase();
    case CONSTANTS.SEGMENT.TAG.MISS:
      return CONSTANTS.SEGMENT.TAG.MISS.toLowerCase();
    case CONSTANTS.SEGMENT.TAG.TARGET:
      return CONSTANTS.SEGMENT.TAG.TARGET.toLowerCase();
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return CONSTANTS.SEGMENT.TAG.VALIDATION.toLowerCase();
    case CONSTANTS.SEGMENT.TAG.ACTUARIAL:
      return CONSTANTS.SEGMENT.TAG.ACTUARIAL.toLowerCase();
    case CONSTANTS.SEGMENT.TAG['HI FREQ']:
      return CONSTANTS.SEGMENT.TAG.ACTUARIAL.toLowerCase();
    case CONSTANTS.SEGMENT.TAG['LO FREQ']:
      return CONSTANTS.SEGMENT.TAG.ACTUARIAL.toLowerCase();
    default:
      return theme;
  }
};

export const colorFromTag = ({ tag }) => {
  switch (tag) {
    case CONSTANTS.SEGMENT.TAG.HIT:
      return 'tagHit';
    case CONSTANTS.SEGMENT.TAG.MISS:
      return 'tagMiss';
    case CONSTANTS.SEGMENT.TAG.TARGET:
      return 'tagTarget';
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return 'tagValidation';
    case CONSTANTS.SEGMENT.TAG.ACTUARIAL:
      return 'tagActuarial';
    case CONSTANTS.SEGMENT.TAG['HI FREQ']:
      return 'tagActuarial';
    case CONSTANTS.SEGMENT.TAG['LO FREQ']:
      return 'tagActuarial';
    default:
      return 'theme_pinpoint';
  }
};

/**
 * getModelTagStyles
 * @param tag {string} - tag value
 * @param extraText {string} - extra text after colon
 * @return {{color: string, viewBox: string, name: string, tip: string}}
 */
export function getModelTagStyles(tag, extraText) {
  switch (tag) {
    case CONSTANTS.TAL_MODEL.TAG.ACTUARIAL:
      return {
        name: 'ActuarialModel',
        color: 'tagActuarial',
        viewBox: '0 0 12 12',
        tip: `${CONSTANTS.TAL_MODEL.TAG_DESC[tag]}: ${extraText}`,
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY:
      return {
        name: 'ActuarialSeverity',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY:
      return {
        name: 'LoFreq',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 12 10',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT:
      return {
        name: 'ActuarialLCM',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT:
      return {
        name: 'ActuarialLCD',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.CLASSIFIER:
      return {
        name: 'ClassifierModel',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 10.46 16',
        tip: `${CONSTANTS.TAL_MODEL.TAG_DESC[tag]}: ${extraText}`,
      };
    default:
      return {
        name: 'Positive',
        color: 'tagHit',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME.HIT,
      };
  }
}

export function getSegmentTagStyles(tag) {
  switch (tag) {
    case CONSTANTS.SEGMENT.TAG.HIT:
      return {
        name: 'Positive',
        color: 'tagHit',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG.CLUSTER:
      return {
        name: 'Clusters',
        color: 'tagHit',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG.MISS:
      return {
        name: 'Negative',
        color: 'tagMiss',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return {
        name: 'Security',
        color: 'tagValidation',
        viewBox: '0 0 10.29 12',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG.TARGET:
      return {
        name: 'TargetNew',
        color: 'tagTarget',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG.ACTUARIAL:
      return {
        name: 'ActuarialModel',
        color: 'tagActuarial',
        viewBox: '0 0 12 12',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case 'ACTVAL':
      return {
        name: 'Security',
        color: 'tagActuarial',
        viewBox: '0 0 12 12',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG['HI FREQ']:
      return {
        name: 'HiFreq',
        color: 'tagActuarial',
        viewBox: '0 0 12 10',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.SEGMENT.TAG['LO FREQ']:
      return {
        name: 'LoFreq',
        color: 'tagActuarial',
        viewBox: '0 0 12 10',
        tip: CONSTANTS.SEGMENT.TAG_NAME[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACTUARIAL:
      return {
        name: 'ActuarialModel',
        color: 'tagActuarial',
        viewBox: '0 0 12 12',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY:
      return {
        name: 'ActuarialSeverity',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY:
      return {
        name: 'LoFreq',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 12 10',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT:
      return {
        name: 'ActuarialLCM',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT:
      return {
        name: 'ActuarialLCD',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 60 50',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    case CONSTANTS.TAL_MODEL.TAG.CLASSIFIER:
      return {
        name: 'ClassifierModel',
        color: CONSTANTS.TAL_MODEL.ACT_STYLING[tag].COLOR,
        viewBox: '0 0 10.46 16',
        tip: CONSTANTS.TAL_MODEL.TAG_DESC[tag],
      };
    default:
      return {
        name: 'Positive',
        color: 'tagHit',
        viewBox: '0 0 50 50',
        tip: CONSTANTS.SEGMENT.TAG_NAME.HIT,
      };
  }
}

/**
 * isValidNumber - check if input val is a valid number or can be coerced to one
 * @param {number|string} val
 * @return {boolean}
 */
export const isValidNumber = (val) => {
  if (typeof val === 'number') return Number.isFinite(val);
  if (typeof val === 'string') return Number.isFinite(parseFloat(val));
  return false;
};

/** identity - return itself
 *
 * @param elem {*}
 * @return {*}
 */
export const identity = (elem) => elem;

/** all - check that all elements of an array are truthy
 *
 * @param array {*[]}
 * @return {boolean}
 */
export const all = (array) => array.every(identity);

/** any - check that any elements of an array are truthy
 *
 * @param array {*[]}
 * @return {boolean}
 */
export const any = (array) => array.some(identity);

/** none - check that no elements of an array are truthy
 *
 * @param array {*[]}
 * @return {boolean}
 */
export const none = (array) => !array.some(identity);

/**
 * valFromQs - extract a value from a query string
 * @param query {URLSearchParams}
 * @param key {string}
 * @param validValues {(string|number|boolean)[]}
 * @param defaultValue {string|number|boolean}
 * @return {*}
 */
export const valFromQs = (query, key, validValues, defaultValue) => {
  const valFromQuery = query.get(key);
  if (valFromQuery) {
    const isValid = validValues.includes(valFromQuery);
    if (isValid) return valFromQuery;
  }
  return defaultValue;
};

export const checkAllTablesRowsExpanded = debounce(
  (row) => {
    const {
      state: { expanded },
      isAllRowsExpanded,
    } = row;
    const anyRowsOpen = Object.keys(expanded)
      .map((key) => expanded[key])
      .some((open) => open);
    return anyRowsOpen || isAllRowsExpanded;
  },
  100,
  { maxWait: 200 }
);

/**
 * unifyApiStatus - return back a unified API status from a list of API statuses:
 * if any FAILED return FAILED
 * if any STARTED, return STARTED
 * if all null, return null
 * if any SUCCEEDED, return SUCCEEDED
 * @param {boolean} debug - print debug statement
 * @param {(string|null)[]} apiStatusList
 */
export function unifyApiStatus(debug = false, ...apiStatusList) {
  // eslint-disable-next-line no-console
  if (debug) console.info(`apiStatusList = ${apiStatusList.map((status) => status || 'null').join(',')}`);
  if (apiStatusList.some((status) => status === CONSTANTS.API.FAILED)) return CONSTANTS.API.FAILED;
  if (apiStatusList.some((status) => status === CONSTANTS.API.STARTED)) return CONSTANTS.API.STARTED;
  if (apiStatusList.every((status) => status === null)) return null;
  if (apiStatusList.some((status) => status === CONSTANTS.API.SUCCEEDED)) return CONSTANTS.API.SUCCEEDED;
  return null;
}

/* eslint-disable no-console */
/**
 * checkHiddenByRole - check if an object should be hidden by role
 *                     true if hidden, else false
 *
 * @param {string} role
 * @param {string} hiddenByRole
 * @param {boolean} useSiteRole - test site roles instead
 * @param {boolean} debug
 * @returns {boolean}
 */
export function checkHiddenByRole(role, hiddenByRole, useSiteRole = false, debug = false) {
  if (!hiddenByRole) {
    if (debug) console.log('hiddenByRole was null: false');
    return false;
  } // In this case it's not supposed to be hidden
  if (hiddenByRole === 'ALL') {
    if (debug) console.log('hiddenByRole was ALL: true');
    return true;
  } // in this case it's hidden for all roles
  const testingList = useSiteRole ? CONSTANTS.SITE_ROLES_HIDDEN : CONSTANTS.ROLES_HIDDEN;
  const roleIdx = testingList.findIndex((name) => name === role);
  const hiddenByIdx = testingList.findIndex((name) => name === hiddenByRole);
  if (debug) {
    console.log('roleIdx was', roleIdx);
    console.log('hiddenByIdx was', hiddenByIdx);
    console.log('returning roleIdx <= hiddenByIdx', roleIdx <= hiddenByIdx);
  }
  return roleIdx <= hiddenByIdx; // in this case, if your role is lte the hiddenRole then true, otherwise false
}
/* eslint-enable no-console */

/* eslint-disable no-console */
/**
 * checkSufficientRole - check if an object should be hidden by role
 *                     true if hidden, else false
 *
 * @param {string} role - your role
 * @param {string} targetRole - the required role to have
 * @param {boolean} useSiteRole - test site roles instead
 * @param {boolean} debug - logging config
 * @returns {boolean}
 */
export function checkSufficientRole(role, targetRole, useSiteRole = false, debug = false) {
  if (!targetRole) {
    if (debug) console.log('targetRole was null: true');
    return true;
  } // In this case it's not supposed to be sufficient
  if (targetRole === 'NONE') {
    if (debug) console.log('targetRole was NONE: false');
    return false;
  } // in this case it's hidden for all roles
  const testingList = useSiteRole ? CONSTANTS.SITE_ROLES_HIDDEN : CONSTANTS.ROLES_HIDDEN;
  const roleIdx = testingList.findIndex((name) => name === role);
  const targetRoleIdx = testingList.findIndex((name) => name === targetRole);
  if (debug) {
    console.log('roleIdx was', roleIdx);
    console.log('targetRoleIdx was', targetRoleIdx);
    console.log('returning roleIdx >= targetRoleIdx', roleIdx >= targetRoleIdx);
  }
  return roleIdx >= targetRoleIdx; // in this case, if your role is gte the targetRole then true, otherwise false
}
/* eslint-enable no-console */

/**
 * queryHelper - helper for setting query string
 * sends query object to callback function for actions
 * then sets query string to url
 * @param {object} history - react-router history object
 * @param {function} action - callback that receives query object
 * @returns {void}
 */
export function queryHelper(history, action) {
  const query = new URLSearchParams(history.location.search);
  const originalAddr = `${history.location.pathname}?${query.toString()}`;
  action(query);
  const newAddr = `${history.location.pathname}?${query.toString()}`;
  if (newAddr !== originalAddr) history.replace(`${history.location.pathname}?${query.toString()}`);
}

/** calcEqPersonsAdj - adjust the losses, premiums, and % pif per deciles in equal persons case
 *
 * @param {number[]} pifPerc
 * @param {number[]} losses
 * @param {number[]} premiums
 * @return {{pinpointPIFPercentile: number[], pinpointPremiums: number[], pinpointLosses: number[]}}
 */
export function calcEqPersonsAdj(pifPerc, losses, premiums) {
  const factors = pifPerc.map((frac) => 0.1 / frac);
  return {
    pinpointPIFPercentile: pifPerc.map((elem, idx) => elem * factors[idx]),
    pinpointPremiums: premiums.map((elem, idx) => elem * factors[idx]),
    pinpointLosses: losses.map((elem, idx) => elem * factors[idx]),
  };
}

/** calcEqPremiumsAdj - adjust the losses, premiums, and % pif per deciles in equal premiums case
 *
 * @param {number[]} pifPerc
 * @param {number[]} losses
 * @param {number[]} premiums
 * @return {{pinpointPIFPercentile: number[], pinpointPremiums: number[], pinpointLosses: number[]}}
 */
export function calcEqPremiumsAdj(pifPerc, losses, premiums) {
  const premiumsExpected = premiums.reduce((acc, cur) => acc + cur, 0) / 10;
  const factors = premiums.map((prem) => premiumsExpected / prem);
  const pif = pifPerc.map((perc) => perc * 100000);
  const adjustedPif = pif.map((policies, idx) => policies * factors[idx]);
  const totalPif = adjustedPif.reduce((acc, cur) => acc + cur, 0);
  return {
    pinpointPIFPercentile: adjustedPif.map((policies) => policies / totalPif),
    pinpointPremiums: premiums.map((elem, idx) => elem * factors[idx]),
    pinpointLosses: losses.map((elem, idx) => elem * factors[idx]),
  };
}
export { createCancellationLadder } from './actuarial_calculator';
